import React, { useState, useRef, useEffect } from "react";
import InforBtn from "@/assets/info-circle.svg";

interface TooltipProps {
  text: string;
  zIndex?: number;
}

const Tooltip: React.FC<TooltipProps> = ({ text, zIndex = 103 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipLeft, setTooltipLeft] = useState(0);
  const [arrowLeft, setArrowLeft] = useState(8);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipId = React.useId();

  useEffect(() => {
    if (!isVisible || !tooltipRef.current || !triggerRef.current) return;

    const calculatePosition = () => {
      const trigger = triggerRef.current!;
      const tooltip = tooltipRef.current!;
      const triggerRect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const triggerWidth = triggerRect.width;
      const margin = 16;

      tooltip.style.visibility = "hidden";
      tooltip.style.opacity = "1";
      const tooltipWidth = tooltip.getBoundingClientRect().width;
      tooltip.style.visibility = "";
      tooltip.style.opacity = "";

      // Default: left-align tooltip with trigger
      let left = 0;

      if (triggerRect.left + tooltipWidth + margin <= viewportWidth) {
        left = 0;
      } else if (triggerRect.right - tooltipWidth >= margin) {
        left = -(tooltipWidth - triggerWidth);
      } else {
        // Center on trigger then clamp to viewport edges
        left = -(tooltipWidth / 2) + triggerWidth / 2;
        const absoluteLeft = triggerRect.left + left;
        if (absoluteLeft < margin) {
          left += margin - absoluteLeft;
        } else if (absoluteLeft + tooltipWidth > viewportWidth - margin) {
          left -= absoluteLeft + tooltipWidth - (viewportWidth - margin);
        }
      }

      // Arrow points at the trigger center regardless of tooltip shift
      const arrowOffset = triggerWidth / 2 - left;
      setTooltipLeft(left);
      setArrowLeft(Math.max(8, Math.min(arrowOffset, tooltipWidth - 16)));
    };

    const id = setTimeout(calculatePosition, 10);
    return () => clearTimeout(id);
  }, [isVisible, text]);

  // Close on outside click/tap (mobile)
  useEffect(() => {
    if (!isVisible) return;
    const close = (e: MouseEvent | TouchEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsVisible(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [isVisible]);

  const zIndexClass = zIndex === 106 ? "z-[106]" : "z-[103]";

  return (
    <button
      type="button"
      ref={triggerRef}
      className="relative inline-block p-0 bg-transparent border-none cursor-pointer"
      aria-expanded={isVisible}
      aria-describedby={tooltipId}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onClick={(e) => {
        e.stopPropagation();
        setIsVisible((v) => !v);
      }}
    >
      <img
        src={InforBtn}
        alt="Information"
        className="inline-block hover:opacity-80 transition-opacity"
      />
      <div
        id={tooltipId}
        role="tooltip"
        ref={tooltipRef}
        style={{ left: `${tooltipLeft}px` }}
        className={`absolute top-full mt-2 bg-black text-white text-[14px] px-3 py-2 rounded shadow-lg transition-opacity duration-300 whitespace-normal pointer-events-none w-max max-w-[min(280px,calc(100vw-32px))] ${zIndexClass} ${isVisible ? "opacity-100" : "opacity-0"}`}
      >
        {text}
        <span
          className="absolute top-0 -translate-y-1/2 h-2 w-2 rotate-45 bg-black"
          style={{ left: `${arrowLeft}px` }}
        />
      </div>
    </button>
  );
};

export default Tooltip;
